export const speechChunks = (text) => {
  const chunks = [];
  let remaining = String(text || "").trim();
  while (remaining) {
    const first = chunks.length === 0;
    const limit = first ? 110 : 220;
    if (remaining.length <= limit) { chunks.push(remaining); break; }
    const preview = remaining.slice(0, limit);
    const sentenceEnd = [...preview.matchAll(/[.!?](?:\s|$)/g)].filter(match => match.index >= 40).at(-1);
    const wordEnd = preview.lastIndexOf(" ", first ? 95 : 180);
    const splitAt = sentenceEnd ? sentenceEnd.index + 1 : wordEnd > 60 ? wordEnd : first ? 100 : 180;
    chunks.push(remaining.slice(0, splitAt).trim());
    remaining = remaining.slice(splitAt).trim();
  }
  return chunks;
};

export const speechAbortError = () => Object.assign(new Error("Speech cancelled"), { name: "AbortError" });

// One current clip and at most one prefetched clip. Rejections are handled
// immediately, even when the prefetched request fails before playback ends.
export async function playSpeech({ text, connected, synthesize, play, browser, signal, onFallback }) {
  const chunks = speechChunks(text);
  if (!chunks.length || signal.aborted) return;
  const network = new AbortController();
  const cancel = () => network.abort();
  signal.addEventListener("abort", cancel, { once: true });
  const check = () => { if (signal.aborted) throw speechAbortError(); };
  const fetchClip = (chunk) => Promise.resolve().then(() => {
    check();
    return synthesize(chunk, { signal: network.signal });
  }).then(blob => ({ blob }), error => ({ error }));
  const fallback = async (index) => {
    network.abort();
    check();
    onFallback?.();
    // Resume at the first unheard chunk, never at the beginning of the reply.
    for (const chunk of chunks.slice(index)) { check(); await browser(chunk); }
  };
  try {
    if (!connected) { await fallback(0); return; }
    let pending = fetchClip(chunks[0]);
    for (let index = 0; index < chunks.length; index += 1) {
      const result = await pending;
      check();
      if (result.error) { await fallback(index); return; }
      pending = chunks[index + 1] ? fetchClip(chunks[index + 1]) : null;
      try { await play(result.blob); }
      catch (error) {
        check();
        // If audio may already have played, replaying the chunk would repeat
        // words. Surface the interruption instead of silently guessing.
        if (error.playbackStarted) throw error;
        await fallback(index);
        return;
      }
      check();
    }
  } finally {
    network.abort();
    signal.removeEventListener("abort", cancel);
  }
}
